import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

export interface BaseInteractiveProps {
  exercise: any;
  onAnswer: (answer: any) => void;
  showFeedback?: boolean;
  disabled?: boolean;
}

// Drag and Drop Component
export const DragDropComponent: React.FC<BaseInteractiveProps> = ({
  exercise,
  onAnswer,
  showFeedback = false,
  disabled = false
}) => {
  const [dropZones, setDropZones] = useState<Record<string, string[]>>(
    exercise.dropZones?.reduce((acc: any, zone: any) => {
      acc[zone.id] = [];
      return acc;
    }, {}) || {}
  );
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Initialize drop zones with any pre-placed items
  useEffect(() => {
    if (exercise.initialPlacements) {
      setDropZones(exercise.initialPlacements);
    }
  }, [exercise.initialPlacements]);

  const handleDragStart = (item: string) => {
    if (disabled) return;
    setDraggedItem(item);
  };

  const handleDrop = (zoneId: string) => {
    if (disabled || !draggedItem) return;

    // Remove item from other zones
    const newDropZones = { ...dropZones };
    Object.keys(newDropZones).forEach(zone => {
      newDropZones[zone] = newDropZones[zone].filter(item => item !== draggedItem);
    });

    // Add to target zone
    newDropZones[zoneId] = [...newDropZones[zoneId], draggedItem];
    setDropZones(newDropZones);
    setDraggedItem(null);
    onAnswer(newDropZones);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <p className="text-gray-700 mb-3">{exercise.content}</p>
        <p className="font-medium text-gray-900 mb-3">{exercise.question}</p>
      </div>

      {/* Draggable Items */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Items to categorize:</h4>
        <div className="flex flex-wrap gap-2">
          {exercise.items?.map((item: string, index: number) => {
            const isPlaced = Object.values(dropZones).some((zone: string[]) => zone.includes(item));
            if (isPlaced) return null;

            return (
              <div
                key={index}
                draggable={!disabled}
                onDragStart={() => handleDragStart(item)}
                className={`px-3 py-2 bg-blue-100 text-blue-800 rounded-lg cursor-move border border-blue-200 ${
                  disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-blue-200'
                }`}
              >
                {item}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drop Zones */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {exercise.dropZones?.map((zone: any) => (
          <div
            key={zone.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(zone.id)}
            className={`min-h-24 p-4 border-2 border-dashed rounded-lg ${
              dropZones[zone.id]?.length > 0
                ? 'border-green-300 bg-green-50'
                : 'border-gray-300 bg-gray-50'
            } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <h4 className="font-medium text-gray-900 mb-2">{zone.label}</h4>
            <div className="space-y-2">
              {dropZones[zone.id]?.map((item: string, index: number) => (
                <div key={index} className="px-2 py-1 bg-white border border-gray-200 rounded text-sm">
                  {item}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showFeedback && exercise.explanation && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">Explanation:</p>
              <p className="text-sm text-blue-700">{exercise.explanation}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};